import { Link } from "@remix-run/react";
import { type MetaFunction } from "@remix-run/node";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { QrCodeIcon, ArrowDownTrayIcon, PrinterIcon, PaintBrushIcon, XCircleIcon, Cog6ToothIcon, SwatchIcon, EyeIcon, Square3Stack3DIcon, ShieldCheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { QRCodeSVG } from 'qrcode.react';
import { motion } from "framer-motion";
import "~/shared/components/dark-mode.css";
import "~/shared/components/animations.css";
import "~/features/calculator/qr-generator.css";
import "~/features/calculator/qr-mobile-enhancements.css";

// Translations for QR Code Generator App
const qrGeneratorTranslations = {
  ar: {
    title: "مولد رمز الاستجابة السريعة",
    description: "أنشئ رموز QR مخصصة بسهولة لمختلف الأغراض.",
    qrType: "نوع رمز الاستجابة السريعة:",
    qrTypeUrl: "رابط (URL)",
    qrTypeText: "نص",
    urlInputLabel: "أدخل الرابط:",
    textInputLabel: "أدخل النص:",
    customizationOptions: "خيارات التخصيص",
    sizeLabel: "الحجم (بكسل):",
    fgColorLabel: "لون الرمز:",
    bgColorLabel: "لون الخلفية:",
    preview: "معاينة:",
    generateQR: "إنشاء الرمز",
    download: "تحميل",
    print: "طباعة",
    comingSoon: "قريباً...",
    resetAll: "إعادة تعيين الخيارات",
    backToMain: "العودة للرئيسية",
    uiLanguageToggle: "Switch to English (UI)",
    darkModeToggle: "تفعيل الوضع المظلم",
    lightModeToggle: "تفعيل الوضع الفاتح",
    qrTypeWifi: "واي فاي",
    qrTypeEmail: "بريد إلكتروني",
    qrTypeWhatsApp: "واتساب",
    qrTypeContact: "جهة اتصال",
    wifiSSID: "اسم الشبكة:",
    wifiPassword: "كلمة المرور:",
    wifiEncryption: "نوع التشفير:",
    emailAddress: "عنوان البريد الإلكتروني:",
    emailSubject: "الموضوع:",
    emailBody: "محتوى البريد:",
    whatsappNumber: "رقم الواتساب:",
    whatsappMessage: "الرسالة:",
    contactName: "الاسم:",
    contactPhone: "رقم الهاتف:",
    contactEmail: "البريد الإلكتروني:",
    contactCompany: "الشركة:",
    contactTitle: "المسمى الوظيفي:",
    contactAddress: "العنوان:",
    contactWebsite: "الموقع الإلكتروني:",
    contactNotes: "ملاحظات:",
    styleOptions: "خيارات التصميم",
    eyeStyle: "نمط العين:",
    eyeStyleSquare: "مربع",
    eyeStyleRounded: "مستدير",
    eyeStyleCircle: "دائري",
    frameStyle: "الإطار:",
    frameStyleNone: "بدون",
    frameStyleSimple: "بسيط",
    frameStyleFancy: "أنيق",
    logoUpload: "تحميل شعار:",
    encoding: "التشفير:",
    encodingUTF8: "UTF-8",
    encodingISO: "ISO-8859-1",
    encodingShiftJIS: "Shift-JIS",
    errorCorrection: "تصحيح الأخطاء:",
    errorCorrectionLow: "منخفض (7%)",
    errorCorrectionMedium: "متوسط (15%)",
    errorCorrectionQuartile: "ربع (25%)",
    errorCorrectionHigh: "عالٍ (30%)",
    // New translations for enhanced customization
    quickPresets: "الأنماط السريعة",
    colorPreview: "معاينة الألوان",
    advancedOptions: "الخيارات المتقدمة",
    basicOptions: "الخيارات الأساسية",

    qrCodeSize: "حجم الرمز",
    qrCodeType: "نوع الرمز",
    qrCodeErrorCorrection: "تصحيح الأخطاء",
    downloadOptions: "خيارات التحميل",
    printOptions: "خيارات الطباعة",
    exportFormats: "صيغ التصدير",
    qualitySettings: "إعدادات الجودة",
    logoSettings: "إعدادات الشعار",
    frameSettings: "إعدادات الإطار",
    eyeSettings: "إعدادات العين",
    encodingSettings: "إعدادات التشفير",
    // Preset names
    presetClassic: "كلاسيكي",
    presetPurple: "بنفسجي",
    presetGreen: "أخضر",
    presetOrange: "برتقالي",
    presetBlue: "أزرق",
    presetPink: "وردي",
    presetDark: "داكن",
    presetLight: "فاتح",
    // Actions
    zoomIn: "تكبير",
    zoomOut: "تصغير",
    applyPreset: "تطبيق النمط",
    clearLogo: "إزالة الشعار",
    previewQR: "معاينة الرمز",
    generateNew: "إنشاء جديد",
    saveSettings: "حفظ الإعدادات",
    loadSettings: "تحميل الإعدادات",
    // Information
    enterDataToPreview: "أدخل البيانات لمعاينة الرمز",
    qrCodeGenerated: "تم إنشاء الرمز بنجاح",
    qrCodeError: "خطأ في إنشاء الرمز",
    fileUploaded: "تم تحميل الملف",
    settingsSaved: "تم حفظ الإعدادات",
    settingsLoaded: "تم تحميل الإعدادات",
    // New Frame Types
    frameModernGradient: "تدرج حديث",
    frameNeonGlow: "توهج نيون",
    frameClassicBorder: "حد كلاسيكي",
    downloadingQR: "جاري تحميل رمز QR...",
    downloadComplete: "تم التحميل بنجاح",
    downloadError: "خطأ في التحميل",
  },
  en: {
    title: "QR Code Generator",
    description: "Easily create custom QR codes for various purposes.",
    qrType: "QR Code Type:",
    qrTypeUrl: "URL",
    qrTypeText: "Text",
    urlInputLabel: "Enter URL:",
    textInputLabel: "Enter Text:",
    customizationOptions: "Customization Options",
    sizeLabel: "Size (pixels):",
    fgColorLabel: "Foreground Color:",
    bgColorLabel: "Background Color:",
    preview: "Preview:",
    generateQR: "Generate QR Code",
    download: "Download",
    print: "Print",
    comingSoon: "Coming soon...",
    resetAll: "Reset Options",
    backToMain: "Back to Main",
    uiLanguageToggle: "التبديل إلى العربية (واجهة المستخدم)",
    darkModeToggle: "Switch to dark mode",
    lightModeToggle: "Switch to light mode",
    qrTypeWifi: "WiFi",
    qrTypeEmail: "Email",
    qrTypeWhatsApp: "WhatsApp",
    qrTypeContact: "Contact",
    wifiSSID: "Network Name:",
    wifiPassword: "Password:",
    wifiEncryption: "Encryption:",
    emailAddress: "Email Address:",
    emailSubject: "Subject:",
    emailBody: "Message:",
    whatsappNumber: "WhatsApp Number:",
    whatsappMessage: "Message:",
    contactName: "Name:",
    contactPhone: "Phone:",
    contactEmail: "Email:",
    contactCompany: "Company:",
    contactTitle: "Title:",
    contactAddress: "Address:",
    contactWebsite: "Website:",
    contactNotes: "Notes:",
    styleOptions: "Style Options",
    eyeStyle: "Eye Style:",
    eyeStyleSquare: "Square",
    eyeStyleRounded: "Rounded",
    eyeStyleCircle: "Circle",
    frameStyle: "Frame:",
    frameStyleNone: "None",
    frameStyleSimple: "Simple",
    frameStyleFancy: "Fancy",
    logoUpload: "Upload Logo:",
    encoding: "Encoding:",
    encodingUTF8: "UTF-8",
    encodingISO: "ISO-8859-1",
    encodingShiftJIS: "Shift-JIS",
    errorCorrection: "Error Correction:",
    errorCorrectionLow: "Low (7%)",
    errorCorrectionMedium: "Medium (15%)",
    errorCorrectionQuartile: "Quartile (25%)",
    errorCorrectionHigh: "High (30%)",
    // New translations for enhanced customization
    quickPresets: "Quick Presets",
    colorPreview: "Color Preview",
    advancedOptions: "Advanced Options",
    basicOptions: "Basic Options",
    qrCodeInfo: "QR Code Information",
    qrCodeSize: "QR Code Size",
    qrCodeType: "QR Code Type",
    qrCodeErrorCorrection: "Error Correction",
    downloadOptions: "Download Options",
    printOptions: "Print Options",
    exportFormats: "Export Formats",
    qualitySettings: "Quality Settings",
    logoSettings: "Logo Settings",
    frameSettings: "Frame Settings",
    eyeSettings: "Eye Settings",
    encodingSettings: "Encoding Settings",
    // Preset names
    presetClassic: "Classic",
    presetPurple: "Purple",
    presetGreen: "Green",
    presetOrange: "Orange",
    presetBlue: "Blue",
    presetPink: "Pink",
    presetDark: "Dark",
    presetLight: "Light",
    // Actions
    zoomIn: "Zoom In",
    zoomOut: "Zoom Out",
    applyPreset: "Apply Preset",
    clearLogo: "Clear Logo",
    previewQR: "Preview QR",
    generateNew: "Generate New",
    saveSettings: "Save Settings",
    loadSettings: "Load Settings",
    // Information
    enterDataToPreview: "Enter data to preview QR code",
    qrCodeGenerated: "QR code generated successfully",
    qrCodeError: "Error generating QR code",
    fileUploaded: "File uploaded successfully",
    settingsSaved: "Settings saved successfully",
    settingsLoaded: "Settings loaded successfully",
    // New Frame Types
    frameModernGradient: "Modern Gradient",
    frameNeonGlow: "Neon Glow",
    frameClassicBorder: "Classic Border",
    downloadingQR: "Downloading QR code...",
    downloadComplete: "Download completed successfully",
    downloadError: "Download error",
  }
};

import { getServiceKeywords, createSEODescription } from "~/shared/utils/seo-keywords";

export const meta: MetaFunction = () => {
  const keywords = getServiceKeywords('qrGenerator');
  const keywordsString = keywords.join(', ');
  const description = createSEODescription('qrGenerator');
  
  return [
    // العنوان الرئيسي المحسن لـ SEO
    { title: "مولد رمز الاستجابة السريعة - أسامة الزبيدي | إنشاء QR كود مجاناً | QR Code Generator - Osama Al-Zubaidi" },
    
    // الوصف المحسن لـ SEO
    { name: "description", content: description },
    
    // الكلمات المفتاحية المحسنة
    { name: "keywords", content: keywordsString },
    
    // معلومات المؤلف
    { name: "author", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { name: "creator", content: "أسامة الزبيدي" },
    { name: "publisher", content: "أسامة الزبيدي" },
    { name: "copyright", content: "صنع بكل ❤️ في 🇸🇦" },
    
    // إعدادات الروبوتات
    { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "googlebot", content: "index, follow" },
    { name: "bingbot", content: "index, follow" },
    
    // معلومات اللغة والموقع
    { name: "language", content: "Arabic, English" },
    { name: "geo.region", content: "SA" },
    { name: "geo.country", content: "Saudi Arabia" },
    
    // Open Graph - محسن لـ SEO
    { property: "og:title", content: "مولد رمز الاستجابة السريعة - أسامة الزبيدي | إنشاء QR كود مجاناً" },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://oz.sa/qr-generator" },
    { property: "og:site_name", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { property: "og:locale", content: "ar_AR" },
    { property: "og:locale:alternate", content: "en_US" },
    { property: "og:image", content: "https://oz.sa/logo-light.png" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: "مولد رمز الاستجابة السريعة - أسامة الزبيدي" },
    { property: "og:image:type", content: "image/png" },
    { property: "og:image:secure_url", content: "https://oz.sa/logo-light.png" },
    
    // Twitter Cards - محسن لـ SEO
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "مولد رمز الاستجابة السريعة - أسامة الزبيدي | إنشاء QR كود مجاناً" },
    { name: "twitter:description", content: description },
    { name: "twitter:creator", content: "@osama_zubaidi" },
    { name: "twitter:site", content: "@osama_zubaidi" },
    { name: "twitter:image", content: "https://oz.sa/logo-light.png" },
    { name: "twitter:image:alt", content: "مولد رمز الاستجابة السريعة - أسامة الزبيدي" },
    { name: "twitter:domain", content: "oz.sa" },
    
    // معلومات إضافية لـ SEO
    { name: "canonical", content: "https://oz.sa/qr-generator" },
    { name: "alternate", content: "https://oz.sa/qr-generator", "hreflang": "ar" },
    { name: "alternate", content: "https://oz.sa/qr-generator", "hreflang": "en" },
    { name: "alternate", content: "https://oz.sa/qr-generator", "hreflang": "x-default" },
    
    // معلومات إضافية للتحسين
    { name: "rating", content: "General" },
    { name: "distribution", content: "Global" },
    { name: "coverage", content: "Worldwide" },
    { name: "target", content: "all" },
  ];
};

export default function QrGeneratorPage() {

  // Default states
  const defaultQrType = "URL";
  const defaultQrValue = "https://oz.sa";
  const defaultQrSize = 256;
  const defaultFgColor = "#000000";
  const defaultBgColor = "#FFFFFF";

  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const storedMode = localStorage.getItem('darkMode');
      return storedMode !== null ? storedMode === 'true' : prefersDark;
    }
    return false;
  });

  const [uiLang, setUiLang] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('pageLang') || 'ar';
    }
    return 'ar';
  });

  const t = qrGeneratorTranslations[uiLang as keyof typeof qrGeneratorTranslations];

  // QR Code State
  const [qrType, setQrType] = useState<string>(defaultQrType);
  const [qrValue, setQrValue] = useState<string>(defaultQrValue);
  // Specific input states for different QR types - will expand later
  const [url, setUrl] = useState<string>(defaultQrValue);
  const [text, setText] = useState<string>("");

  // Customization State
  const [size, setSize] = useState<number>(defaultQrSize);
  const [fgColor, setFgColor] = useState<string>(defaultFgColor);
  const [bgColor, setBgColor] = useState<string>(defaultBgColor);


  // Preview & Output State
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null); // For image preview
  const qrPreviewRef = useRef<HTMLDivElement>(null); // To get SVG/Canvas for download

  // New QR types state
  const [wifiData, setWifiData] = useState({
    ssid: "",
    password: "",
    encryption: "WPA"
  });
  
  const [emailData, setEmailData] = useState({
    address: "",
    subject: "",
    body: ""
  });
  
  const [whatsappData, setWhatsappData] = useState({
    number: "",
    message: ""
  });
  
  const [contactData, setContactData] = useState({
    name: "",
    phone: "",
    email: "",
    company: "",
    title: "",
    address: "",
    website: "",
    notes: ""
  });

  // New QR types state
  const [phoneNumber, setPhoneNumber] = useState("");
  
  const [smsData, setSmsData] = useState({
    number: "",
    message: ""
  });
  
  const [geoData, setGeoData] = useState({
    latitude: "",
    longitude: "",
    name: ""
  });
  
  const [calendarData, setCalendarData] = useState({
    title: "",
    date: "",
    time: "",
    location: "",
    description: ""
  });
  
  const [appStoreData, setAppStoreData] = useState({
    store: "ios",
    appId: ""
  });
  
  const [socialData, setSocialData] = useState({
    platform: "instagram",
    username: ""
  });

  // Enhanced customization state
  const [eyeStyle, setEyeStyle] = useState("square");
  const [frameStyle, setFrameStyle] = useState("none");
  const [logo, setLogo] = useState<File | null>(null);
  const [errorCorrection, setErrorCorrection] = useState("M");
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [logoOpacity, setLogoOpacity] = useState(1);
  const [logoSize, setLogoSize] = useState(0.2);
  const [frameColor, setFrameColor] = useState("#000000");
  const [frameAccentColor, setFrameAccentColor] = useState("#f97316");
  const [eyeColor, setEyeColor] = useState("#000000");

  const [qrQuality, setQrQuality] = useState("high");
  
  // New advanced features
  const [qrShape, setQrShape] = useState<"square" | "circle" | "rounded" | "diamond">("square");
  const [gradientType, setGradientType] = useState<"none" | "linear" | "radial" | "rainbow">("none");
  const [gradientColors, setGradientColors] = useState(["#8B5CF6", "#EC4899"]);
  const [qrStyle, setQrStyle] = useState<"normal" | "dots" | "squares" | "circles" | "stars" | "hearts">("normal");
  const [cornerRadius, setCornerRadius] = useState(0);
  const [patternDensity, setPatternDensity] = useState(1);
  const [animationType, setAnimationType] = useState<"none" | "pulse" | "rotate" | "bounce">("none");

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
    localStorage.setItem('darkMode', isDarkMode.toString());
  }, [isDarkMode]);

  useEffect(() => {
    document.documentElement.lang = uiLang;
    document.documentElement.dir = uiLang === 'ar' ? 'rtl' : 'ltr';
    localStorage.setItem('pageLang', uiLang);
  }, [uiLang]);

  const toggleDarkMode = () => setIsDarkMode(prev => !prev);
  const toggleUiLanguage = () => {
    setUiLang(prev => (prev === 'ar' ? 'en' : 'ar'));
    // Reset any specific errors or generated data on language change if needed
  };







  const handleDownload = async (format: "svg" | "png" | "jpg" | "webp") => {
    if (!qrValue) {
      alert(uiLang === 'ar' ? 'يرجى إدخال بيانات أولاً' : 'Please enter data first');
      return;
    }

    try {
      // Get the entire QR container including frames
      const qrContainer = qrPreviewRef.current?.querySelector('.qr-code-enhanced');
      const svgElement = qrContainer?.querySelector('svg');
      
      if (!svgElement || !qrContainer) {
        alert(uiLang === 'ar' ? 'خطأ في العثور على رمز QR' : 'Error finding QR code');
        return;
      }

      if (format === 'svg') {
        // For SVG, create a complete SVG with frame information
        // Create new SVG that includes frame styling
        const newSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        newSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        newSvg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
        newSvg.setAttribute('width', size.toString());
        newSvg.setAttribute('height', size.toString());
        newSvg.setAttribute('viewBox', `0 0 ${size} ${size}`);
        
        // Add background
        const backgroundRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        backgroundRect.setAttribute('width', '100%');
        backgroundRect.setAttribute('height', '100%');
        backgroundRect.setAttribute('fill', bgColor);
        newSvg.appendChild(backgroundRect);
        
        // Add frame background if needed
        if (frameStyle !== 'none') {
          const frameGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
          frameGroup.setAttribute('class', `qr-frame-${frameStyle}`);
          
          // Add frame styling based on frame type
          if (frameStyle === 'concentric-circles' || frameStyle === 'circular-with-text' || frameStyle === 'premium-circular') {
            // NEW CIRCULAR FRAME DESIGN - Professional Look
            
            // Outer decorative ring for concentric circles
            if (frameStyle === 'concentric-circles') {
              const outerRing = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
              outerRing.setAttribute('cx', (size / 2).toString());
              outerRing.setAttribute('cy', (size / 2).toString());
              outerRing.setAttribute('r', (size * 0.48).toString());
              outerRing.setAttribute('fill', 'none');
              outerRing.setAttribute('stroke', frameColor);
              outerRing.setAttribute('stroke-width', '4');
              outerRing.setAttribute('opacity', '0.4');
              frameGroup.appendChild(outerRing);
            }
            
            // Main background circle
            const mainCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            mainCircle.setAttribute('cx', (size / 2).toString());
            mainCircle.setAttribute('cy', (size / 2).toString());
            mainCircle.setAttribute('r', (size * 0.43).toString());
            
            if (frameStyle === 'concentric-circles') {
              mainCircle.setAttribute('fill', `url(#orangeGradient-${size})`);
            } else if (frameStyle === 'circular-with-text') {
              mainCircle.setAttribute('fill', `url(#greenGradient-${size})`);
            } else if (frameStyle === 'premium-circular') {
              mainCircle.setAttribute('fill', `url(#premiumGradient-${size})`);
            }
            frameGroup.appendChild(mainCircle);
            
            // White ring for circular-with-text
            if (frameStyle === 'circular-with-text') {
              const whiteRing = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
              whiteRing.setAttribute('cx', (size / 2).toString());
              whiteRing.setAttribute('cy', (size / 2).toString());
              whiteRing.setAttribute('r', (size * 0.39).toString());
              whiteRing.setAttribute('fill', 'none');
              whiteRing.setAttribute('stroke', 'white');
              whiteRing.setAttribute('stroke-width', '3');
              whiteRing.setAttribute('opacity', '0.7');
              frameGroup.appendChild(whiteRing);
            }
            
            // Inner white circle (QR background)
            const innerCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            innerCircle.setAttribute('cx', (size / 2).toString());
            innerCircle.setAttribute('cy', (size / 2).toString());
            
            if (frameStyle === 'concentric-circles') {
              innerCircle.setAttribute('r', (size * 0.32).toString());
            } else if (frameStyle === 'circular-with-text') {
              innerCircle.setAttribute('r', (size * 0.30).toString());
            } else if (frameStyle === 'premium-circular') {
              innerCircle.setAttribute('r', (size * 0.30).toString());
            }
            
            innerCircle.setAttribute('fill', 'white');
            frameGroup.appendChild(innerCircle);
            
            // Add gradients to defs
            const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
            
            // Orange gradient for concentric circles
            const orangeGrad = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
            orangeGrad.setAttribute('id', `orangeGradient-${size}`);
            orangeGrad.setAttribute('x1', '0%');
            orangeGrad.setAttribute('y1', '0%');
            orangeGrad.setAttribute('x2', '100%');
            orangeGrad.setAttribute('y2', '100%');
            
            const orangeStop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
            orangeStop1.setAttribute('offset', '0%');
            orangeStop1.setAttribute('stop-color', '#f97316');
            const orangeStop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
            orangeStop2.setAttribute('offset', '100%');
            orangeStop2.setAttribute('stop-color', '#ea580c');
            
            orangeGrad.appendChild(orangeStop1);
            orangeGrad.appendChild(orangeStop2);
            defs.appendChild(orangeGrad);
            
            // Green gradient for circular-with-text
            const greenGrad = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
            greenGrad.setAttribute('id', `greenGradient-${size}`);
            greenGrad.setAttribute('x1', '0%');
            greenGrad.setAttribute('y1', '0%');
            greenGrad.setAttribute('x2', '100%');
            greenGrad.setAttribute('y2', '100%');
            
            const greenStop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
            greenStop1.setAttribute('offset', '0%');
            greenStop1.setAttribute('stop-color', '#22c55e');
            const greenStop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
            greenStop2.setAttribute('offset', '100%');
            greenStop2.setAttribute('stop-color', '#16a34a');
            
            greenGrad.appendChild(greenStop1);
            greenGrad.appendChild(greenStop2);
            defs.appendChild(greenGrad);
            
            // Premium gradient
            const premiumGrad = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
            premiumGrad.setAttribute('id', `premiumGradient-${size}`);
            premiumGrad.setAttribute('x1', '0%');
            premiumGrad.setAttribute('y1', '0%');
            premiumGrad.setAttribute('x2', '100%');
            premiumGrad.setAttribute('y2', '100%');
            
            const premiumStop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
            premiumStop1.setAttribute('offset', '0%');
            premiumStop1.setAttribute('stop-color', '#ec4899');
            const premiumStop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
            premiumStop2.setAttribute('offset', '50%');
            premiumStop2.setAttribute('stop-color', '#f97316');
            const premiumStop3 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
            premiumStop3.setAttribute('offset', '100%');
            premiumStop3.setAttribute('stop-color', '#8b5cf6');
            
            premiumGrad.appendChild(premiumStop1);
            premiumGrad.appendChild(premiumStop2);
            premiumGrad.appendChild(premiumStop3);
            defs.appendChild(premiumGrad);
            
            newSvg.appendChild(defs);
          } else if (frameStyle === 'rectangular' || frameStyle === 'with-text' || frameStyle === 'premium-rectangular') {
            // Rectangular frame
            const frameRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            frameRect.setAttribute('x', (size * 0.05).toString());
            frameRect.setAttribute('y', (size * 0.05).toString());
            frameRect.setAttribute('width', (size * 0.9).toString());
            frameRect.setAttribute('height', (size * 0.9).toString());
            frameRect.setAttribute('fill', frameColor);
            frameRect.setAttribute('rx', '12');
            frameGroup.appendChild(frameRect);
            
            // Add accent bar
            const accentBar = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            accentBar.setAttribute('x', (size * 0.05).toString());
            accentBar.setAttribute('y', (size * 0.05).toString());
            accentBar.setAttribute('width', (size * 0.9).toString());
            accentBar.setAttribute('height', '16');
            accentBar.setAttribute('fill', frameAccentColor);
            accentBar.setAttribute('rx', '12');
            frameGroup.appendChild(accentBar);
          } else if (frameStyle === 'modern-gradient' || frameStyle === 'neon-glow' || frameStyle === 'classic-border') {
            // Modern frame styles
            const modernRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            modernRect.setAttribute('x', '0');
            modernRect.setAttribute('y', '0');
            modernRect.setAttribute('width', size.toString());
            modernRect.setAttribute('height', size.toString());
            modernRect.setAttribute('fill', frameColor);
            modernRect.setAttribute('rx', '20');
            frameGroup.appendChild(modernRect);
          }
          
          newSvg.appendChild(frameGroup);
        }
        
        // Clone and add the QR code SVG content
        const qrGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        
        // Adjust QR scale based on frame type
        let qrScale = 0.7;
        if (frameStyle === 'concentric-circles') {
          qrScale = 0.5; // Smaller for better circular fit
        } else if (frameStyle === 'circular-with-text') {
          qrScale = 0.45; // Even smaller for text space
        } else if (frameStyle === 'premium-circular') {
          qrScale = 0.48; // Balanced for premium look
        } else if (frameStyle !== 'none') {
          qrScale = 0.6; // Other frames
        } else {
          qrScale = 0.9; // No frame
        }
        
        const qrOffset = size * (1 - qrScale) / 2;
        qrGroup.setAttribute('transform', `translate(${qrOffset}, ${qrOffset}) scale(${qrScale})`);
        
        // Copy QR code content
        const clonedQRContent = svgElement.cloneNode(true) as SVGElement;
        Array.from(clonedQRContent.children).forEach(child => {
          qrGroup.appendChild(child.cloneNode(true));
        });
        
        newSvg.appendChild(qrGroup);
        
        // Add SCAN ME text for circular frames
        if (frameStyle === 'concentric-circles' || frameStyle === 'circular-with-text' || frameStyle === 'premium-circular') {
          const scanText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
          scanText.setAttribute('x', (size / 2).toString());
          scanText.setAttribute('y', (size * 0.85).toString());
          scanText.setAttribute('text-anchor', 'middle');
          scanText.setAttribute('font-family', 'Arial, sans-serif');
          scanText.setAttribute('font-weight', '700');
          scanText.setAttribute('font-size', (size * 0.04).toString());
          scanText.setAttribute('letter-spacing', '2px');
          
          if (frameStyle === 'concentric-circles') {
            scanText.setAttribute('fill', '#f97316');
          } else if (frameStyle === 'circular-with-text') {
            scanText.setAttribute('fill', '#22c55e');
          } else if (frameStyle === 'premium-circular') {
            scanText.setAttribute('fill', '#ec4899');
          }
          
          scanText.textContent = 'SCAN ME';
          newSvg.appendChild(scanText);
        }
        
        const svgData = new XMLSerializer().serializeToString(newSvg);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `qrcode-${Date.now()}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        // For PNG, JPG, WEBP - capture the entire QR container including frames
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { alpha: format !== 'jpg' });
        if (!ctx) {
          alert(uiLang === 'ar' ? 'خطأ في إنشاء Canvas' : 'Error creating canvas');
          return;
        }

        // Calculate optimal size with quality settings
        const qualityMultiplier = qrQuality === 'ultra' ? 6 : qrQuality === 'high' ? 4 : qrQuality === 'medium' ? 3 : 2;
        const baseSize = Math.max(size, 256);
        const canvasSize = baseSize * qualityMultiplier;
        
        // Set canvas dimensions
        canvas.width = canvasSize;
        canvas.height = canvasSize;
        
        // Enable high-quality rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Draw background
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, canvasSize, canvasSize);

        // Draw frame if present
        if (frameStyle !== 'none') {
          // Note: drawFrameToCanvas functionality integrated inline
          if (frameStyle === 'concentric-circles' || frameStyle === 'circular-with-text' || frameStyle === 'premium-circular') {
            // NEW CIRCULAR FRAME DESIGN for Canvas
            const centerX = canvasSize / 2;
            const centerY = canvasSize / 2;
            
            // Draw decorative outer ring for concentric circles
            if (frameStyle === 'concentric-circles') {
              ctx.beginPath();
              ctx.arc(centerX, centerY, canvasSize * 0.48, 0, 2 * Math.PI);
              ctx.strokeStyle = frameColor;
              ctx.lineWidth = 8;
              ctx.globalAlpha = 0.4;
              ctx.stroke();
              ctx.globalAlpha = 1;
            }
            
            // Main background circle with gradient
            const gradient = ctx.createLinearGradient(0, 0, canvasSize, canvasSize);
            if (frameStyle === 'concentric-circles') {
              gradient.addColorStop(0, '#f97316');
              gradient.addColorStop(1, '#ea580c');
            } else if (frameStyle === 'circular-with-text') {
              gradient.addColorStop(0, '#22c55e');
              gradient.addColorStop(1, '#16a34a');
            } else if (frameStyle === 'premium-circular') {
              gradient.addColorStop(0, '#ec4899');
              gradient.addColorStop(0.5, '#f97316');
              gradient.addColorStop(1, '#8b5cf6');
            }
            
            ctx.beginPath();
            ctx.arc(centerX, centerY, canvasSize * 0.43, 0, 2 * Math.PI);
            ctx.fillStyle = gradient;
            ctx.fill();
            
            // White ring for circular-with-text
            if (frameStyle === 'circular-with-text') {
              ctx.beginPath();
              ctx.arc(centerX, centerY, canvasSize * 0.39, 0, 2 * Math.PI);
              ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
              ctx.lineWidth = 6;
              ctx.stroke();
            }
            
            // Inner white circle (QR background)
            let innerRadius = canvasSize * 0.30; // default
            if (frameStyle === 'concentric-circles') {
              innerRadius = canvasSize * 0.32;
            } else if (frameStyle === 'circular-with-text') {
              innerRadius = canvasSize * 0.30;
            } else if (frameStyle === 'premium-circular') {
              innerRadius = canvasSize * 0.30;
            }
            
            ctx.beginPath();
            ctx.arc(centerX, centerY, innerRadius, 0, 2 * Math.PI);
            ctx.fillStyle = 'white';
            ctx.fill();
            
          } else if (frameStyle === 'rectangular' || frameStyle === 'with-text' || frameStyle === 'premium-rectangular') {
            // Draw rectangular frame
            const frameMargin = canvasSize * 0.05;
            const frameWidth = canvasSize * 0.9;
            const frameHeight = canvasSize * 0.9;
            
            // Main frame
            ctx.fillStyle = frameColor;
            ctx.fillRect(frameMargin, frameMargin, frameWidth, frameHeight);
            
            // Accent bar
            ctx.fillStyle = frameAccentColor;
            ctx.fillRect(frameMargin, frameMargin, frameWidth, 16);
            
          } else if (frameStyle === 'modern-gradient') {
            // Modern gradient frame
            const gradient = ctx.createLinearGradient(0, 0, canvasSize, canvasSize);
            gradient.addColorStop(0, '#667eea');
            gradient.addColorStop(0.25, '#764ba2');
            gradient.addColorStop(0.5, '#f093fb');
            gradient.addColorStop(0.75, '#f5576c');
            gradient.addColorStop(1, '#4facfe');
            
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, canvasSize, canvasSize);
            
          } else if (frameStyle === 'neon-glow') {
            // Neon glow frame
            ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
            ctx.fillRect(0, 0, canvasSize, canvasSize);
            
            // Neon border
            ctx.strokeStyle = '#00ffff';
            ctx.lineWidth = 6;
            ctx.shadowBlur = 20;
            ctx.shadowColor = '#00ffff';
            ctx.strokeRect(20, 20, canvasSize - 40, canvasSize - 40);
            
          } else if (frameStyle === 'classic-border') {
            // Classic border frame
            ctx.fillStyle = '#f5f5dc';
            ctx.fillRect(0, 0, canvasSize, canvasSize);
            
            // Double border
            ctx.strokeStyle = frameColor;
            ctx.lineWidth = 8;
            ctx.strokeRect(15, 15, canvasSize - 30, canvasSize - 30);
            
            ctx.strokeStyle = frameAccentColor;
            ctx.lineWidth = 4;
            ctx.strokeRect(25, 25, canvasSize - 50, canvasSize - 50);
          }
        }

        // Create QR SVG for canvas rendering
        const qrSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        qrSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        qrSvg.setAttribute('width', canvasSize.toString());
        qrSvg.setAttribute('height', canvasSize.toString());
        qrSvg.setAttribute('viewBox', `0 0 ${canvasSize} ${canvasSize}`);
        
        // Copy QR content with proper scaling based on frame type
        let qrScale = 0.9;
        if (frameStyle === 'concentric-circles') {
          qrScale = 0.5;
        } else if (frameStyle === 'circular-with-text') {
          qrScale = 0.45;
        } else if (frameStyle === 'premium-circular') {
          qrScale = 0.48;
        } else if (frameStyle !== 'none') {
          qrScale = 0.6;
        }
        
        const qrOffset = canvasSize * (1 - qrScale) / 2;
        const qrGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        qrGroup.setAttribute('transform', `translate(${qrOffset}, ${qrOffset}) scale(${qrScale})`);
        
        // Clone QR code content
        const originalQRContent = svgElement.cloneNode(true) as SVGElement;
        Array.from(originalQRContent.children).forEach(child => {
          qrGroup.appendChild(child.cloneNode(true));
        });
        
        qrSvg.appendChild(qrGroup);

        const svgData = new XMLSerializer().serializeToString(qrSvg);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);

        const img = new Image();
        img.onload = () => {
          // Draw QR code over the frame
          ctx.drawImage(img, 0, 0, canvasSize, canvasSize);
          
          // Add SCAN ME text for circular frames
          if (frameStyle === 'concentric-circles' || frameStyle === 'circular-with-text' || frameStyle === 'premium-circular') {
            ctx.save();
            ctx.font = `bold ${canvasSize * 0.04}px Arial, sans-serif`;
            ctx.textAlign = 'center';
            ctx.letterSpacing = '2px';
            
            if (frameStyle === 'concentric-circles') {
              ctx.fillStyle = '#f97316';
            } else if (frameStyle === 'circular-with-text') {
              ctx.fillStyle = '#22c55e';
            } else if (frameStyle === 'premium-circular') {
              ctx.fillStyle = '#ec4899';
            }
            
            ctx.fillText('SCAN ME', canvasSize / 2, canvasSize * 0.85);
            ctx.restore();
          }

          // Convert to desired format with quality settings
          const quality = qrQuality === 'ultra' ? 1.0 : qrQuality === 'high' ? 0.95 : qrQuality === 'medium' ? 0.85 : 0.75;
          
          canvas.toBlob((blob) => {
            if (blob) {
              const downloadUrl = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = downloadUrl;
              link.download = `qrcode-${Date.now()}.${format}`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              // Cleanup
              URL.revokeObjectURL(downloadUrl);
              URL.revokeObjectURL(url);
            } else {
              alert(uiLang === 'ar' ? 'خطأ في تحويل الصورة' : 'Error converting image');
            }
          }, `image/${format}`, quality);
        };

        img.onerror = () => {
          alert(uiLang === 'ar' ? 'خطأ في تحميل الصورة' : 'Error loading image');
          URL.revokeObjectURL(url);
        };

        // Set crossOrigin to handle CORS properly
        img.crossOrigin = 'anonymous';
        img.src = url;
      }
    } catch (error) {
      console.error('Download error:', error);
      alert(uiLang === 'ar' ? 'خطأ في التنزيل' : 'Download error');
    }
  };

  const handlePrint = () => {
    if (!qrCodeDataUrl) return;
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Print QR Code</title>
            <style>
              body { display: flex; justify-content: center; align-items: center; height: 100vh; margin: 0; }
              img { max-width: 100%; max-height: 100%; }
            </style>
          </head>
          <body>
            <img src="${qrCodeDataUrl}" alt="QR Code" />
            <script>
              window.onload = function() {
                window.print();
                window.onafterprint = function() {
                  window.close();
                };
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };
  
  const handleResetOptions = () => {
    setQrType(defaultQrType);
    setUrl(defaultQrValue);
    setText("");
    setSize(defaultQrSize);
    setFgColor(defaultFgColor);
    setBgColor(defaultBgColor);
    setQrValue(defaultQrValue);
    setQrCodeDataUrl(null);
    
    // Reset advanced options
    setEyeStyle("square");
    setFrameStyle("none");
    setLogo(null);
    setErrorCorrection("M");
    setLogoOpacity(1);
    setLogoSize(0.2);
    setFrameColor("#000000");
    setFrameAccentColor("#f97316");
    setEyeColor("#000000");
    setQrQuality("high");
    setQrShape("square");
    setGradientType("none");
    setGradientColors(["#8B5CF6", "#EC4899"]);
    setQrStyle("normal");
    setCornerRadius(0);
    setPatternDensity(1);
    setAnimationType("none");
  };

  // Enhanced QR code value generation with memoization
  const generateQrValue = useCallback(() => {
    switch (qrType) {
      case "URL":
        return url;
      case "Text":
        return text;
      case "WiFi":
        return `WIFI:S:${wifiData.ssid};T:${wifiData.encryption};P:${wifiData.password};;`;
      case "Email":
        return `mailto:${emailData.address}?subject=${encodeURIComponent(emailData.subject)}&body=${encodeURIComponent(emailData.body)}`;
      case "WhatsApp":
        return `https://wa.me/${whatsappData.number}?text=${encodeURIComponent(whatsappData.message)}`;
      case "Contact":
        return `BEGIN:VCARD\nVERSION:3.0\nFN:${contactData.name}\nTEL:${contactData.phone}\nEMAIL:${contactData.email}\nORG:${contactData.company}\nTITLE:${contactData.title}\nADR:${contactData.address}\nURL:${contactData.website}\nNOTE:${contactData.notes}\nEND:VCARD`;
      case "Phone":
        return `tel:${phoneNumber}`;
      case "SMS":
        return `sms:${smsData.number}?body=${encodeURIComponent(smsData.message)}`;
      case "GeoLocation":
        return `geo:${geoData.latitude},${geoData.longitude}?q=${encodeURIComponent(geoData.name)}`;
      case "Calendar": {
        const eventDate = new Date(`${calendarData.date}T${calendarData.time}`);
        const endDate = new Date(eventDate.getTime() + 60 * 60 * 1000); // 1 hour duration
        return `BEGIN:VEVENT\nSUMMARY:${calendarData.title}\nDTSTART:${eventDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z\nDTEND:${endDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z\nLOCATION:${calendarData.location}\nDESCRIPTION:${calendarData.description}\nEND:VEVENT`;
      }
      case "AppStore": {
        const storeUrls = {
          ios: `https://apps.apple.com/app/id${appStoreData.appId}`,
          android: `https://play.google.com/store/apps/details?id=${appStoreData.appId}`,
          huawei: `https://appgallery.huawei.com/#/app/${appStoreData.appId}`,
          samsung: `https://galaxystore.samsung.com/detail/${appStoreData.appId}`
        };
        return storeUrls[appStoreData.store as keyof typeof storeUrls] || storeUrls.ios;
      }
      case "SocialMedia": {
        const socialUrls = {
          instagram: `https://instagram.com/${socialData.username}`,
          twitter: `https://twitter.com/${socialData.username}`,
          facebook: `https://facebook.com/${socialData.username}`,
          linkedin: `https://linkedin.com/in/${socialData.username}`,
          youtube: `https://youtube.com/@${socialData.username}`,
          tiktok: `https://tiktok.com/@${socialData.username}`,
          snapchat: `https://snapchat.com/add/${socialData.username}`
        };
        return socialUrls[socialData.platform as keyof typeof socialUrls] || socialUrls.instagram;
      }
      default:
        return "";
    }
  }, [qrType, url, text, wifiData, emailData, whatsappData, contactData, phoneNumber, smsData, geoData, calendarData, appStoreData, socialData]);

  // Update qrValue when inputs change
  useEffect(() => {
    const newValue = generateQrValue();
    setQrValue(newValue);
  }, [generateQrValue]);

  // Force re-render when pattern style changes
  useEffect(() => {
    if (qrPreviewRef.current) {
      const svgElement = qrPreviewRef.current.querySelector('svg');
      if (svgElement) {
        // Force re-render by updating the SVG
        const svgData = new XMLSerializer().serializeToString(svgElement);
        const dataUrl = `data:image/svg+xml;base64,${btoa(svgData)}`;
        setQrCodeDataUrl(dataUrl);
      }
    }
  }, [qrStyle, patternDensity, qrShape, gradientType, animationType, cornerRadius]);

  // Update qrValue when advanced options change
  useEffect(() => {
    const newValue = generateQrValue();
    setQrValue(newValue);
  }, [generateQrValue]);



  // Memoized QR code settings for performance
  const qrCodeSettings = useMemo(() => ({
    value: qrValue,
    size: size,
    fgColor: fgColor,
    bgColor: bgColor,
    level: errorCorrection as "L" | "M" | "Q" | "H",
    includeMargin: true,

    imageSettings: logo ? {
      src: URL.createObjectURL(logo),
      height: size * logoSize,
      width: size * logoSize,
      excavate: true,
      opacity: logoOpacity,
    } : undefined
  }), [qrValue, size, fgColor, bgColor, errorCorrection, logo, logoSize, logoOpacity]);



  // Function to handle file uploads
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setLogo(file);
    }
  };




  // Render input fields based on QR type
  const renderInputFields = () => {
    switch (qrType) {
      case "URL":
        return (
          <div className="mb-6">
            <label htmlFor="urlInput" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t.urlInputLabel}
            </label>
            <input 
              type="url" 
              id="urlInput" 
              value={url} 
              onChange={(e) => setUrl(e.target.value)} 
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
              placeholder="https://example.com" 
            />
          </div>
        );
      case "Text":
        return (
          <div className="mb-6">
            <label htmlFor="textInput" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t.textInputLabel}
            </label>
            <textarea 
              id="textInput" 
              value={text} 
              onChange={(e) => setText(e.target.value)} 
              rows={3} 
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
              placeholder="Enter your text here"
            />
          </div>
        );
      case "WiFi":
        return (
          <div className="space-y-4">
            <div className="mb-4">
              <label htmlFor="wifiSSID" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.wifiSSID}
              </label>
              <input 
                type="text" 
                id="wifiSSID" 
                value={wifiData.ssid} 
                onChange={(e) => setWifiData({...wifiData, ssid: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
              />
            </div>
            <div className="mb-4">
              <label htmlFor="wifiPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.wifiPassword}
              </label>
              <input 
                type="password" 
                id="wifiPassword" 
                value={wifiData.password} 
                onChange={(e) => setWifiData({...wifiData, password: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
              />
            </div>
            <div className="mb-4">
              <label htmlFor="wifiEncryption" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.wifiEncryption}
              </label>
              <select 
                id="wifiEncryption" 
                value={wifiData.encryption} 
                onChange={(e) => setWifiData({...wifiData, encryption: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="WPA">WPA/WPA2</option>
                <option value="WEP">WEP</option>
                <option value="nopass">No Password</option>
              </select>
            </div>
          </div>
        );
      case "Email":
        return (
          <div className="space-y-4">
            <div className="mb-4">
              <label htmlFor="emailAddress" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.emailAddress}
              </label>
              <input 
                type="email" 
                id="emailAddress" 
                value={emailData.address} 
                onChange={(e) => setEmailData({...emailData, address: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
              />
            </div>
            <div className="mb-4">
              <label htmlFor="emailSubject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.emailSubject}
              </label>
              <input 
                type="text" 
                id="emailSubject" 
                value={emailData.subject} 
                onChange={(e) => setEmailData({...emailData, subject: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
              />
            </div>
            <div className="mb-4">
              <label htmlFor="emailBody" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.emailBody}
              </label>
              <textarea 
                id="emailBody" 
                value={emailData.body} 
                onChange={(e) => setEmailData({...emailData, body: e.target.value})} 
                rows={3} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
        );
      case "WhatsApp":
        return (
          <div className="space-y-4">
            <div className="mb-4">
              <label htmlFor="whatsappNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.whatsappNumber}
              </label>
              <input 
                type="tel" 
                id="whatsappNumber" 
                value={whatsappData.number} 
                onChange={(e) => setWhatsappData({...whatsappData, number: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
                placeholder="+1234567890" 
              />
            </div>
            <div className="mb-4">
              <label htmlFor="whatsappMessage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.whatsappMessage}
              </label>
              <textarea 
                id="whatsappMessage" 
                value={whatsappData.message} 
                onChange={(e) => setWhatsappData({...whatsappData, message: e.target.value})} 
                rows={3} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
        );
      case "Contact":
        return (
          <div className="space-y-4">
            <div className="mb-4">
              <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.contactName}
              </label>
              <input 
                type="text" 
                id="contactName" 
                value={contactData.name} 
                onChange={(e) => setContactData({...contactData, name: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
              />
            </div>
            <div className="mb-4">
              <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.contactPhone}
              </label>
              <input 
                type="tel" 
                id="contactPhone" 
                value={contactData.phone} 
                onChange={(e) => setContactData({...contactData, phone: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
              />
            </div>
            <div className="mb-4">
              <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.contactEmail}
              </label>
              <input 
                type="email" 
                id="contactEmail" 
                value={contactData.email} 
                onChange={(e) => setContactData({...contactData, email: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
              />
            </div>
            <div className="mb-4">
              <label htmlFor="contactCompany" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.contactCompany}
              </label>
              <input 
                type="text" 
                id="contactCompany" 
                value={contactData.company} 
                onChange={(e) => setContactData({...contactData, company: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
              />
            </div>
            <div className="mb-4">
              <label htmlFor="contactTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.contactTitle}
              </label>
              <input 
                type="text" 
                id="contactTitle" 
                value={contactData.title} 
                onChange={(e) => setContactData({...contactData, title: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
              />
            </div>
            <div className="mb-4">
              <label htmlFor="contactAddress" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.contactAddress}
              </label>
              <textarea 
                id="contactAddress" 
                value={contactData.address} 
                onChange={(e) => setContactData({...contactData, address: e.target.value})} 
                rows={2} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="mb-4">
              <label htmlFor="contactWebsite" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.contactWebsite}
              </label>
              <input 
                type="url" 
                id="contactWebsite" 
                value={contactData.website} 
                onChange={(e) => setContactData({...contactData, website: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
              />
            </div>
            <div className="mb-4">
              <label htmlFor="contactNotes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.contactNotes}
              </label>
              <textarea 
                id="contactNotes" 
                value={contactData.notes} 
                onChange={(e) => setContactData({...contactData, notes: e.target.value})} 
                rows={2} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
        );
      case "Phone":
        return (
          <div className="mb-6">
            <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              رقم الهاتف:
            </label>
            <input 
              type="tel" 
              id="phoneNumber" 
              value={phoneNumber} 
              onChange={(e) => setPhoneNumber(e.target.value)} 
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
              placeholder="+966501234567" 
            />
          </div>
        );
      case "SMS":
        return (
          <div className="space-y-4">
            <div className="mb-4">
              <label htmlFor="smsNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                رقم الهاتف:
              </label>
              <input 
                type="tel" 
                id="smsNumber" 
                value={smsData.number} 
                onChange={(e) => setSmsData({...smsData, number: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
                placeholder="+966501234567" 
              />
            </div>
            <div className="mb-4">
              <label htmlFor="smsMessage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                الرسالة:
              </label>
              <textarea 
                id="smsMessage" 
                value={smsData.message} 
                onChange={(e) => setSmsData({...smsData, message: e.target.value})} 
                rows={3} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
                placeholder="أدخل رسالتك هنا"
              />
            </div>
          </div>
        );
      case "GeoLocation":
        return (
          <div className="space-y-4">
            <div className="mb-4">
              <label htmlFor="latitude" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                خط العرض:
              </label>
              <input 
                type="number" 
                id="latitude" 
                value={geoData.latitude} 
                onChange={(e) => setGeoData({...geoData, latitude: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
                placeholder="24.7136" 
                step="0.0001"
              />
            </div>
            <div className="mb-4">
              <label htmlFor="longitude" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                خط الطول:
              </label>
              <input 
                type="number" 
                id="longitude" 
                value={geoData.longitude} 
                onChange={(e) => setGeoData({...geoData, longitude: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
                placeholder="46.6753" 
                step="0.0001"
              />
            </div>
            <div className="mb-4">
              <label htmlFor="locationName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                اسم الموقع:
              </label>
              <input 
                type="text" 
                id="locationName" 
                value={geoData.name} 
                onChange={(e) => setGeoData({...geoData, name: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
                placeholder="الرياض، المملكة العربية السعودية"
              />
            </div>
          </div>
        );
      case "Calendar":
        return (
          <div className="space-y-4">
            <div className="mb-4">
              <label htmlFor="eventTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                عنوان الحدث:
              </label>
              <input 
                type="text" 
                id="eventTitle" 
                value={calendarData.title} 
                onChange={(e) => setCalendarData({...calendarData, title: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
                placeholder="اجتماع العمل"
              />
            </div>
            <div className="mb-4">
              <label htmlFor="eventDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                التاريخ:
              </label>
              <input 
                type="date" 
                id="eventDate" 
                value={calendarData.date} 
                onChange={(e) => setCalendarData({...calendarData, date: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
              />
            </div>
            <div className="mb-4">
              <label htmlFor="eventTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                الوقت:
              </label>
              <input 
                type="time" 
                id="eventTime" 
                value={calendarData.time} 
                onChange={(e) => setCalendarData({...calendarData, time: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
              />
            </div>
            <div className="mb-4">
              <label htmlFor="eventLocation" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                الموقع:
              </label>
              <input 
                type="text" 
                id="eventLocation" 
                value={calendarData.location} 
                onChange={(e) => setCalendarData({...calendarData, location: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
                placeholder="مكتب الشركة"
              />
            </div>
            <div className="mb-4">
              <label htmlFor="eventDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                الوصف:
              </label>
              <textarea 
                id="eventDescription" 
                value={calendarData.description} 
                onChange={(e) => setCalendarData({...calendarData, description: e.target.value})} 
                rows={3} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
                placeholder="وصف الحدث"
              />
            </div>
          </div>
        );
      case "AppStore":
        return (
          <div className="space-y-4">
            <div className="mb-4">
              <label htmlFor="appStore" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                نوع المتجر:
              </label>
              <select 
                id="appStore" 
                value={appStoreData.store} 
                onChange={(e) => setAppStoreData({...appStoreData, store: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="ios">App Store (iOS)</option>
                <option value="android">Google Play (Android)</option>
                <option value="huawei">Huawei AppGallery</option>
                <option value="samsung">Samsung Galaxy Store</option>
              </select>
            </div>
            <div className="mb-4">
              <label htmlFor="appId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                معرف التطبيق:
              </label>
              <input 
                type="text" 
                id="appId" 
                value={appStoreData.appId} 
                onChange={(e) => setAppStoreData({...appStoreData, appId: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
                placeholder="com.example.app"
              />
            </div>
          </div>
        );
      case "SocialMedia":
        return (
          <div className="space-y-4">
            <div className="mb-4">
              <label htmlFor="socialPlatform" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                المنصة:
              </label>
              <select 
                id="socialPlatform" 
                value={socialData.platform} 
                onChange={(e) => setSocialData({...socialData, platform: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="instagram">Instagram</option>
                <option value="twitter">Twitter</option>
                <option value="facebook">Facebook</option>
                <option value="linkedin">LinkedIn</option>
                <option value="youtube">YouTube</option>
                <option value="tiktok">TikTok</option>
                <option value="snapchat">Snapchat</option>
              </select>
            </div>
            <div className="mb-4">
              <label htmlFor="socialUsername" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                اسم المستخدم:
              </label>
              <input 
                type="text" 
                id="socialUsername" 
                value={socialData.username} 
                onChange={(e) => setSocialData({...socialData, username: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white" 
                placeholder="@username"
              />
            </div>
          </div>
        );
      default:
        return <p>Select a QR code type.</p>;
    }
  };



  return (
    <div className={`qr-generator-app ${isDarkMode ? 'dark' : ''}`} dir={uiLang === 'ar' ? 'rtl' : 'ltr'}>
      {/* Simple App Header Instead of Hero Section */}
      <div className="flex items-center justify-center py-6 gap-3">
        <span className="text-3xl lg:text-4xl" role="img" aria-label="QR Code Icon">📱</span>
        <h1 className="text-2xl lg:text-3xl font-bold text-purple-700 dark:text-purple-300 tracking-tight">
          {t.title}
        </h1>
      </div>

      {/* Header Controls */}
      <div className="qr-header">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="qr-btn" title={t.backToMain}>
            {uiLang === 'ar' ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h17" />
              </svg>
            )}
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={toggleUiLanguage} className="qr-lang-switcher" aria-label={t.uiLanguageToggle}>
              {uiLang === 'ar' ? 'EN (UI)' : 'عربي (UI)'}
            </button>
            <button onClick={toggleDarkMode} className="qr-btn" aria-label={isDarkMode ? t.lightModeToggle : t.darkModeToggle}>
              {isDarkMode ? (
                <svg className="w-5 h-5 text-yellow-400 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="qr-main-content qr-scale-in">
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8">
            
            {/* Enhanced Customization Panel - Left Side */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="lg:col-span-1"
            >
              <div className="qr-customization-panel-enhanced-v3 qr-fade-in-v3">
                
                {/* Panel Header with Enhanced Design */}
                <div className="qr-panel-header-enhanced-v3">
                  <div className="qr-panel-header-content-v3">
                    <h2 className="qr-panel-title-v3">
                      {t.customizationOptions}
                    </h2>
                    <div className="qr-panel-actions-v3">
                      <motion.button 
                        onClick={() => setShowAdvancedOptions(prev => !prev)}
                        className={`qr-action-btn-v3 ${showAdvancedOptions ? 'active' : ''}`}
                        title={showAdvancedOptions ? 'إخفاء الخيارات المتقدمة' : 'الخيارات المتقدمة'}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <motion.div
                          animate={{ rotate: showAdvancedOptions ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Cog6ToothIcon className="w-4 h-4" />
                        </motion.div>
                      </motion.button>
                      <motion.button 
                        onClick={handleResetOptions}
                        className="qr-action-btn-v3"
                        title={t.resetAll}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <XCircleIcon className="w-4 h-4" />
                      </motion.button>
                      
                      {/* Mobile Quick Toggle */}
                      <div className="md:hidden">
                        <motion.button
                          onClick={() => setShowAdvancedOptions(prev => !prev)}
                          className="qr-mobile-quick-toggle"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <span className="text-xs font-bold">
                            {showAdvancedOptions ? 'بسيط' : 'متقدم'}
                          </span>
                          <motion.div
                            animate={{ rotate: showAdvancedOptions ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <ChevronDownIcon className="w-3 h-3" />
                          </motion.div>
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Enhanced Presets Section */}
                <div className="qr-presets-section-v3">
                  <div className="qr-presets-title-v3">
                    <QrCodeIcon className="w-5 h-5" />
                    {t.qrCodeType}
                  </div>
                  
                  {/* Selected QR Type Display */}
                  {qrType && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      className="mb-4"
                    >
                      <div className="qr-selected-type-display">
                        <div className="qr-selected-type-content">
                          <span className="qr-selected-type-icon text-2xl">
                            {[
                              { value: 'URL', icon: '🌐' },
                              { value: 'Text', icon: '📝' },
                              { value: 'WiFi', icon: '📶' },
                              { value: 'Email', icon: '📧' },
                              { value: 'WhatsApp', icon: '💬' },
                              { value: 'Contact', icon: '👤' },
                              { value: 'Phone', icon: '📞' },
                              { value: 'SMS', icon: '💌' },
                              { value: 'GeoLocation', icon: '📍' },
                              { value: 'Calendar', icon: '📅' },
                              { value: 'AppStore', icon: '📱' },
                              { value: 'SocialMedia', icon: '📢' }
                            ].find(t => t.value === qrType)?.icon}
                          </span>
                          <div className="qr-selected-type-info">
                            <span className="qr-selected-type-label">
                              {[
                                { value: 'URL', label: t.qrTypeUrl },
                                { value: 'Text', label: t.qrTypeText },
                                { value: 'WiFi', label: t.qrTypeWifi },
                                { value: 'Email', label: t.qrTypeEmail },
                                { value: 'WhatsApp', label: t.qrTypeWhatsApp },
                                { value: 'Contact', label: t.qrTypeContact },
                                { value: 'Phone', label: 'رقم الهاتف' },
                                { value: 'SMS', label: 'رسالة نصية' },
                                { value: 'GeoLocation', label: 'الموقع الجغرافي' },
                                { value: 'Calendar', label: 'التقويم' },
                                { value: 'AppStore', label: 'متجر التطبيقات' },
                                { value: 'SocialMedia', label: 'وسائل التواصل' }
                              ].find(t => t.value === qrType)?.label}
                            </span>
                            <span className="qr-selected-type-description">
                              {[
                                { value: 'URL', description: 'روابط المواقع' },
                                { value: 'Text', description: 'نصوص عادية' },
                                { value: 'WiFi', description: 'شبكات الواي فاي' },
                                { value: 'Email', description: 'رسائل البريد' },
                                { value: 'WhatsApp', description: 'رسائل الواتساب' },
                                { value: 'Contact', description: 'جهات الاتصال' },
                                { value: 'Phone', description: 'أرقام الهواتف' },
                                { value: 'SMS', description: 'رسائل SMS' },
                                { value: 'GeoLocation', description: 'إحداثيات GPS' },
                                { value: 'Calendar', description: 'مواعيد الأحداث' },
                                { value: 'AppStore', description: 'روابط التطبيقات' },
                                { value: 'SocialMedia', description: 'حسابات التواصل' }
                              ].find(t => t.value === qrType)?.description}
                            </span>
                          </div>
                          <button
                            onClick={() => setQrType('')}
                            className="qr-change-type-btn"
                            title="تغيير النوع"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                  
                  {/* QR Types Grid */}
                  <motion.div
                    initial={{ opacity: 1, height: 'auto' }}
                    animate={{ 
                      opacity: qrType ? 0 : 1, 
                      height: qrType ? 0 : 'auto',
                      scale: qrType ? 0.8 : 1
                    }}
                    transition={{ duration: 0.3 }}
                    className="qr-presets-grid-v3"
                    style={{ 
                      overflow: qrType ? 'hidden' : 'visible',
                      pointerEvents: qrType ? 'none' : 'auto'
                    }}
                  >
                    {[
                      { value: 'URL', label: t.qrTypeUrl, icon: '🌐', description: 'روابط المواقع' },
                      { value: 'Text', label: t.qrTypeText, icon: '📝', description: 'نصوص عادية' },
                      { value: 'WiFi', label: t.qrTypeWifi, icon: '📶', description: 'شبكات الواي فاي' },
                      { value: 'Email', label: t.qrTypeEmail, icon: '📧', description: 'رسائل البريد' },
                      { value: 'WhatsApp', label: t.qrTypeWhatsApp, icon: '💬', description: 'رسائل الواتساب' },
                      { value: 'Contact', label: t.qrTypeContact, icon: '👤', description: 'جهات الاتصال' },
                      { value: 'Phone', label: 'رقم الهاتف', icon: '📞', description: 'أرقام الهواتف' },
                      { value: 'SMS', label: 'رسالة نصية', icon: '💌', description: 'رسائل SMS' },
                      { value: 'GeoLocation', label: 'الموقع الجغرافي', icon: '📍', description: 'إحداثيات GPS' },
                      { value: 'Calendar', label: 'التقويم', icon: '📅', description: 'مواعيد الأحداث' },
                      { value: 'AppStore', label: 'متجر التطبيقات', icon: '📱', description: 'روابط التطبيقات' },
                      { value: 'SocialMedia', label: 'وسائل التواصل', icon: '📢', description: 'حسابات التواصل' }
                    ].map((type) => (
                      <motion.button
                        key={type.value}
                        onClick={() => setQrType(type.value)}
                        className={`qr-preset-btn-v3 ${qrType === type.value ? 'active' : ''}`}
                        title={type.description}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        layout
                      >
                        <span className="text-lg mb-1">{type.icon}</span>
                        <span className="text-xs">{type.label}</span>
                      </motion.button>
                    ))}
                  </motion.div>
                </div>

                {/* Enhanced Settings Container */}
                <div className="qr-settings-container-v3">
                  


                  {/* Dynamic Input Fields */}
                  <motion.div
                    key={qrType}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="qr-fade-in-v3"
                  >
                    {renderInputFields()}
                  </motion.div>



                  {/* Basic Customization */}
                  <div className="qr-settings-group-v3 qr-fade-in-v3">
                    <div className="qr-settings-group-header-v3">
                      <div className="qr-settings-group-icon-v3">
                        <Square3Stack3DIcon className="w-4 h-4" />
                      </div>
                      <h3 className="qr-settings-group-title-v3">{t.basicOptions}</h3>
                    </div>
                    
                    {/* Size Control */}
                    <div className="qr-form-group-v3">
                      <div className="flex items-center justify-between">
                        <label className="qr-form-label-v3">{t.qrCodeSize}</label>
                        <span className="text-sm font-bold text-purple-600 dark:text-purple-400">
                          {size}px
                        </span>
                      </div>
                      <input
                        type="range"
                        min="50"
                        max="1000"
                        value={size}
                        onChange={(e) => setSize(parseInt(e.target.value, 10) || 0)}
                        step="10"
                        className="qr-range-slider-v3"
                      />
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span>50px</span>
                        <span>256px</span>
                        <span>512px</span>
                        <span>1000px</span>
                      </div>
                    </div>

                    {/* Color Controls */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="qr-form-group-v3">
                        <label className="qr-form-label-v3">{t.fgColorLabel}</label>
                        <input 
                          type="color" 
                          value={fgColor} 
                          onChange={(e) => setFgColor(e.target.value)} 
                          className="qr-color-picker-v3"
                        />
                      </div>
                      
                      <div className="qr-form-group-v3">
                        <label className="qr-form-label-v3">{t.bgColorLabel}</label>
                        <input 
                          type="color" 
                          value={bgColor} 
                          onChange={(e) => setBgColor(e.target.value)} 
                          className="qr-color-picker-v3"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Advanced Options */}
                  {showAdvancedOptions && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="qr-fade-in-v3"
                    >
                      {/* Quick Color Presets */}
                      <div className="qr-settings-group-v3">
                        <div className="qr-settings-group-header-v3">
                          <div className="qr-settings-group-icon-v3">
                            <SwatchIcon className="w-4 h-4" />
                          </div>
                          <h3 className="qr-settings-group-title-v3">{t.quickPresets}</h3>
                        </div>
                        <div className="qr-presets-grid-v3">
                          {[
                            { name: 'classic', label: t.presetClassic, fg: '#000000', bg: '#FFFFFF' },
                            { name: 'purple', label: t.presetPurple, fg: '#8B5CF6', bg: '#F3E8FF' },
                            { name: 'green', label: t.presetGreen, fg: '#10B981', bg: '#ECFDF5' },
                            { name: 'orange', label: t.presetOrange, fg: '#F59E0B', bg: '#FFFBEB' },
                            { name: 'blue', label: t.presetBlue, fg: '#3B82F6', bg: '#EFF6FF' },
                            { name: 'pink', label: t.presetPink, fg: '#EC4899', bg: '#FDF2F8' },
                            { name: 'dark', label: t.presetDark, fg: '#FFFFFF', bg: '#1F2937' },
                            { name: 'light', label: t.presetLight, fg: '#374151', bg: '#F9FAFB' }
                          ].map((preset) => (
                            <button
                              key={preset.name}
                              onClick={() => {
                                setFgColor(preset.fg);
                                setBgColor(preset.bg);
                              }}
                              className={`qr-preset-btn-v3 ${fgColor === preset.fg && bgColor === preset.bg ? 'active' : ''}`}
                            >
                              <div className="flex items-center justify-center w-4 h-4 rounded border-2 border-current mb-1">
                                <div 
                                  className="w-2 h-2 rounded-sm"
                                  style={{ backgroundColor: preset.fg }}
                                />
                              </div>
                              <span className="text-xs">{preset.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Error Correction */}
                      <div className="qr-settings-group-v3">
                        <div className="qr-settings-group-header-v3">
                          <div className="qr-settings-group-icon-v3">
                            <ShieldCheckIcon className="w-4 h-4" />
                          </div>
                          <h3 className="qr-settings-group-title-v3">{t.qrCodeErrorCorrection}</h3>
                        </div>
                        <div className="qr-form-group-v3">
                          <select
                            value={errorCorrection}
                            onChange={(e) => setErrorCorrection(e.target.value)}
                            className="qr-form-select-v3"
                          >
                            <option value="L">{t.errorCorrectionLow}</option>
                            <option value="M">{t.errorCorrectionMedium}</option>
                            <option value="Q">{t.errorCorrectionQuartile}</option>
                            <option value="H">{t.errorCorrectionHigh}</option>
                          </select>
                        </div>
                      </div>

                      {/* Logo Settings */}
                      <div className="qr-settings-group-v3">
                        <div className="qr-settings-group-header-v3">
                          <div className="qr-settings-group-icon-v3">
                            <PaintBrushIcon className="w-4 h-4" />
                          </div>
                          <h3 className="qr-settings-group-title-v3">{t.logoSettings}</h3>
                        </div>
                                                 <div className="qr-form-group-v3">
                           <div className="qr-file-upload-v3">
                             <input
                               type="file"
                               accept="image/*"
                               onChange={handleFileUpload}
                               id="logo-upload"
                             />
                             <label htmlFor="logo-upload" className="qr-file-upload-label-v3">
                               <span>📁</span>
                               <span>{logo ? 'تم تحميل الشعار' : 'اختر شعاراً'}</span>
                             </label>
                           </div>
                         </div>
                        {logo && (
                          <div className="grid grid-cols-2 gap-4">
                            <div className="qr-form-group-v3">
                              <label htmlFor="logo-size" className="qr-form-label-v3">حجم الشعار</label>
                              <input
                                id="logo-size"
                                type="range"
                                min="0.1"
                                max="0.5"
                                step="0.05"
                                value={logoSize}
                                onChange={(e) => setLogoSize(parseFloat(e.target.value))}
                                className="qr-range-slider-v3"
                              />
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {Math.round(logoSize * 100)}%
                              </div>
                            </div>
                            <div className="qr-form-group-v3">
                              <label htmlFor="logo-opacity" className="qr-form-label-v3">شفافية الشعار</label>
                              <input
                                id="logo-opacity"
                                type="range"
                                min="0.1"
                                max="1"
                                step="0.1"
                                value={logoOpacity}
                                onChange={(e) => setLogoOpacity(parseFloat(e.target.value))}
                                className="qr-range-slider-v3"
                              />
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {Math.round(logoOpacity * 100)}%
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Frame Settings */}
                      <div className="qr-settings-group-v3">
                        <div className="qr-settings-group-header-v3">
                          <div className="qr-settings-group-icon-v3">
                            <Square3Stack3DIcon className="w-4 h-4" />
                          </div>
                          <h3 className="qr-settings-group-title-v3">{t.frameSettings}</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="qr-form-group-v3">
                            <label className="qr-form-label-v3">{t.frameStyle}</label>
                            <select
                              value={frameStyle}
                              onChange={(e) => setFrameStyle(e.target.value)}
                              className="qr-form-select-v3"
                            >
                              <option value="none">{t.frameStyleNone}</option>
                              <option value="simple">{t.frameStyleSimple}</option>
                              <option value="fancy">{t.frameStyleFancy}</option>
                              <option value="rectangular">مستطيل مع شريط</option>
                              <option value="concentric-circles">دوائر متحدة المركز</option>
                              <option value="with-text">مستطيل مع نص</option>
                              <option value="circular-with-text">دائري مع نص</option>
                              <option value="premium-rectangular">مستطيل مميز</option>
                              <option value="premium-circular">دائري مميز</option>
                              <option value="modern-gradient">{t.frameModernGradient}</option>
                              <option value="neon-glow">{t.frameNeonGlow}</option>
                              <option value="classic-border">{t.frameClassicBorder}</option>
                            </select>
                          </div>
                          {frameStyle !== 'none' && (
                            <div className="qr-form-group-v3">
                              <label htmlFor="frame-color" className="qr-form-label-v3">لون الإطار</label>
                              <input 
                                id="frame-color"
                                type="color" 
                                value={frameColor} 
                                onChange={(e) => setFrameColor(e.target.value)} 
                                className="qr-color-picker-v3"
                              />
                            </div>
                          )}
                          {(frameStyle === 'rectangular' || frameStyle === 'with-text' || frameStyle === 'concentric-circles' || frameStyle === 'circular-with-text') && (
                            <div className="qr-form-group-v3">
                              <label htmlFor="frame-accent-color" className="qr-form-label-v3">لون الشريط</label>
                              <input 
                                id="frame-accent-color"
                                type="color" 
                                value={frameAccentColor} 
                                onChange={(e) => setFrameAccentColor(e.target.value)} 
                                className="qr-color-picker-v3"
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Eye Settings */}
                      <div className="qr-settings-group-v3">
                        <div className="qr-settings-group-header-v3">
                          <div className="qr-settings-group-icon-v3">
                            <EyeIcon className="w-4 h-4" />
                          </div>
                          <h3 className="qr-settings-group-title-v3">{t.eyeSettings}</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="qr-form-group-v3">
                            <label htmlFor="eye-style" className="qr-form-label-v3">{t.eyeStyle}</label>
                            <select
                              id="eye-style"
                              value={eyeStyle}
                              onChange={(e) => setEyeStyle(e.target.value)}
                              className="qr-form-select-v3"
                            >
                              <option value="square">{t.eyeStyleSquare}</option>
                              <option value="rounded">{t.eyeStyleRounded}</option>
                              <option value="circle">{t.eyeStyleCircle}</option>
                            </select>
                          </div>
                          <div className="qr-form-group-v3">
                            <label htmlFor="eye-color" className="qr-form-label-v3">لون العين</label>
                            <input 
                              id="eye-color"
                              type="color" 
                              value={eyeColor} 
                              onChange={(e) => setEyeColor(e.target.value)} 
                              className="qr-color-picker-v3"
                            />
                          </div>
                        </div>
                      </div>

                      {/* QR Shape */}
                      <div className="qr-settings-group-v3">
                        <div className="qr-settings-group-header-v3">
                          <div className="qr-settings-group-icon-v3">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                            </svg>
                          </div>
                          <h3 className="qr-settings-group-title-v3">شكل الرمز</h3>
                        </div>
                                                  <div className="qr-form-group-v3">
                            <label htmlFor="qr-shape" className="qr-form-label-v3">نوع الشكل</label>
                            <select
                              id="qr-shape"
                            value={qrShape}
                            onChange={(e) => setQrShape(e.target.value as "square" | "circle" | "rounded" | "diamond")}
                            className="qr-form-select-v3"
                          >
                            <option value="square">مربع</option>
                            <option value="circle">دائرة</option>
                            <option value="rounded">مستدير</option>
                            <option value="diamond">معين</option>
                          </select>
                        </div>
                        {qrShape === "rounded" && (
                          <div className="qr-form-group-v3">
                            <label htmlFor="corner-radius" className="qr-form-label-v3">نصف قطر الزوايا</label>
                            <input
                              id="corner-radius"
                              type="range"
                              min="0"
                              max="20"
                              value={cornerRadius}
                              onChange={(e) => setCornerRadius(parseInt(e.target.value))}
                              className="qr-range-slider-v3"
                            />
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {cornerRadius}px
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Gradient Colors */}
                      <div className="qr-settings-group-v3">
                        <div className="qr-settings-group-header-v3">
                          <div className="qr-settings-group-icon-v3">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
                            </svg>
                          </div>
                          <h3 className="qr-settings-group-title-v3">الألوان المتدرجة</h3>
                        </div>
                                                  <div className="qr-form-group-v3">
                            <label htmlFor="gradient-type" className="qr-form-label-v3">نوع التدرج</label>
                            <select
                              id="gradient-type"
                            value={gradientType}
                            onChange={(e) => setGradientType(e.target.value as "none" | "linear" | "radial" | "rainbow")}
                            className="qr-form-select-v3"
                          >
                            <option value="none">بدون تدرج</option>
                            <option value="linear">خطي</option>
                            <option value="radial">دائري</option>
                            <option value="rainbow">قوس قزح</option>
                          </select>
                        </div>
                        {gradientType !== "none" && gradientType !== "rainbow" && (
                          <div className="qr-form-group-v3">
                            <label htmlFor="gradient-color-1" className="qr-form-label-v3">الألوان</label>
                            <div className="flex gap-2">
                              <input
                                id="gradient-color-1"
                                type="color"
                                value={gradientColors[0]}
                                onChange={(e) => setGradientColors([e.target.value, gradientColors[1]])}
                                className="qr-color-picker-v3 w-12 h-10"
                              />
                              <input
                                id="gradient-color-2"
                                type="color"
                                value={gradientColors[1]}
                                onChange={(e) => setGradientColors([gradientColors[0], e.target.value])}
                                className="qr-color-picker-v3 w-12 h-10"
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* QR Style */}
                      <div className="qr-settings-group-v3">
                        <div className="qr-settings-group-header-v3">
                          <div className="qr-settings-group-icon-v3">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                            </svg>
                          </div>
                          <h3 className="qr-settings-group-title-v3">نمط الرمز</h3>
                        </div>
                                                  <div className="qr-form-group-v3">
                            <label htmlFor="qr-style" className="qr-form-label-v3">نوع النمط</label>
                            <select
                              id="qr-style"
                            value={qrStyle}
                            onChange={(e) => setQrStyle(e.target.value as "normal" | "dots" | "squares" | "circles" | "stars" | "hearts")}
                            className="qr-form-select-v3"
                          >
                            <option value="normal">عادي</option>
                            <option value="dots">نقاط</option>
                            <option value="squares">مربعات</option>
                            <option value="circles">دوائر</option>
                            <option value="stars">نجوم</option>
                            <option value="hearts">قلوب</option>
                          </select>
                        </div>
                        {qrStyle !== "normal" && (
                          <div className="qr-form-group-v3">
                            <label htmlFor="pattern-density" className="qr-form-label-v3">كثافة النمط</label>
                            <input
                              id="pattern-density"
                              type="range"
                              min="0.5"
                              max="2"
                              step="0.1"
                              value={patternDensity}
                              onChange={(e) => setPatternDensity(parseFloat(e.target.value))}
                              className="qr-range-slider-v3"
                            />
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {patternDensity}x
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Animation */}
                      <div className="qr-settings-group-v3">
                        <div className="qr-settings-group-header-v3">
                          <div className="qr-settings-group-icon-v3">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                          </div>
                          <h3 className="qr-settings-group-title-v3">الحركة</h3>
                        </div>
                        <div className="qr-form-group-v3">
                          <label htmlFor="animation-type" className="qr-form-label-v3">نوع الحركة</label>
                          <select
                            id="animation-type"
                            value={animationType}
                            onChange={(e) => setAnimationType(e.target.value as "none" | "pulse" | "rotate" | "bounce")}
                            className="qr-form-select-v3"
                          >
                            <option value="none">بدون حركة</option>
                            <option value="pulse">نبض</option>
                            <option value="rotate">دوران</option>
                            <option value="bounce">ارتداد</option>
                          </select>
                        </div>
                      </div>

                      {/* Quality Settings */}
                      <div className="qr-settings-group-v3">
                        <div className="qr-settings-group-header-v3">
                          <div className="qr-settings-group-icon-v3">
                            <Cog6ToothIcon className="w-4 h-4" />
                          </div>
                          <h3 className="qr-settings-group-title-v3">{t.qualitySettings}</h3>
                        </div>
                        <div className="qr-form-group-v3">
                          <label htmlFor="qr-quality" className="qr-form-label-v3">جودة التصدير</label>
                          <select
                            id="qr-quality"
                            value={qrQuality}
                            onChange={(e) => setQrQuality(e.target.value)}
                            className="qr-form-select-v3"
                          >
                            <option value="low">منخفضة</option>
                            <option value="medium">متوسطة</option>
                            <option value="high">عالية</option>
                            <option value="ultra">فائقة</option>
                          </select>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Quick Actions Footer */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700 mt-6">
                    <button 
                      onClick={handleResetOptions}
                      className="qr-btn-v3 qr-btn-secondary-v3"
                    >
                      <XCircleIcon className="w-4 h-4" />
                      <span>{t.resetAll}</span>
                    </button>
                    
                    <button 
                      onClick={() => setShowAdvancedOptions(prev => !prev)}
                      className="qr-btn-v3"
                    >
                      <Cog6ToothIcon className="w-4 h-4" />
                      <span>{showAdvancedOptions ? 'إخفاء المتقدم' : 'الخيارات المتقدمة'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* QR Code Preview - Center */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="lg:col-span-1 flex flex-col items-center"
            >
              <h2 className="text-xl lg:text-2xl font-bold text-gray-800 dark:text-white mb-4 lg:mb-6">
                {t.preview}
              </h2>
              
              <div className="relative">
                {/* Preview Container with enhanced styling */}
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl lg:rounded-3xl shadow-xl lg:shadow-2xl p-4 lg:p-8 border border-gray-200 dark:border-gray-700 min-h-[400px] lg:min-h-[600px] flex items-center justify-center overflow-hidden">
                  <div
                    ref={qrPreviewRef}
                    className="flex justify-center items-center p-4 lg:p-8 bg-white dark:bg-gray-700 rounded-xl lg:rounded-2xl shadow-lg lg:shadow-xl border-2 lg:border-4 border-white dark:border-gray-600"
                    style={{
                      boxShadow: '0 15px 30px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      minWidth: '250px',
                      minHeight: '250px',
                      maxWidth: '400px',
                      maxHeight: '400px'
                    }}
                  >
                    {qrValue ? (
                      <div 
                        className={`qr-code-enhanced ${qrShape !== 'square' ? `qr-shape-${qrShape}` : ''} ${gradientType !== 'none' ? `qr-gradient-${gradientType}` : ''} ${qrStyle !== 'normal' ? `qr-pattern-${qrStyle}` : ''} ${animationType !== 'none' ? `qr-animation-${animationType}` : ''} ${qrStyle !== 'normal' ? `qr-pattern-density-${patternDensity.toString().replace('.', '-')}` : ''} ${frameStyle !== 'none' ? `qr-frame-${frameStyle}` : ''}`}
                        style={{
                          '--corner-radius': `${cornerRadius}px`,
                          '--gradient-color-1': gradientColors[0],
                          '--gradient-color-2': gradientColors[1],
                          '--pattern-density': patternDensity,
                          '--frame-color': frameColor,
                          '--frame-accent-color': frameAccentColor,
                          minWidth: frameStyle === 'concentric-circles' || frameStyle === 'circular-with-text' || frameStyle === 'premium-circular' ? '320px' : 'auto',
                          minHeight: frameStyle === 'concentric-circles' || frameStyle === 'circular-with-text' || frameStyle === 'premium-circular' ? '320px' : 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        } as React.CSSProperties}
                        key={`qr-${qrStyle}-${patternDensity}-${frameStyle}-${frameColor}-${frameAccentColor}`}
                      >
                        <QRCodeSVG
                          {...qrCodeSettings}
                        />
                      </div>
                    ) : (
                      <div className="text-center text-gray-500 dark:text-gray-400">
                        <QrCodeIcon className="w-24 h-24 lg:w-32 lg:h-32 mx-auto mb-3 lg:mb-4 opacity-50" />
                        <p className="text-sm lg:text-lg font-medium">
                          {uiLang === 'ar' ? 'أدخل البيانات لمعاينة الرمز' : 'Enter data to preview QR code'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* QR Code Information */}
                <div className="mt-4 lg:mt-6 text-center space-y-3">
                  <div className="bg-white dark:bg-gray-800 rounded-lg lg:rounded-xl p-3 lg:p-4 shadow-md lg:shadow-lg border border-gray-200 dark:border-gray-700">
                    <p className="text-xs lg:text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {uiLang === 'ar' ? 'معلومات الرمز' : 'QR Code Information'}
                    </p>
                    <div className="flex items-center justify-center space-x-2 lg:space-x-4 text-xs text-gray-500 dark:text-gray-500">
                      <span className="flex items-center space-x-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                        </svg>
                        <span>{size}×{size}px</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <span>{qrType}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
                        </svg>
                        <span>{errorCorrection}</span>
                      </span>
                    </div>
                  </div>
                  
                  {/* Download Buttons */}
                  {qrValue && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.2 }}
                      className="space-y-3"
                    >
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-3">
                        <button onClick={() => handleDownload("svg")} className="bg-green-600 text-white py-2 px-2 lg:px-3 rounded-lg text-xs lg:text-sm font-medium hover:bg-green-700 transition-colors flex items-center justify-center space-x-1">
                          <ArrowDownTrayIcon className="w-3 h-3 lg:w-4 lg:h-4" />
                          <span>SVG</span>
                        </button>
                        <button onClick={() => handleDownload("png")} className="bg-blue-600 text-white py-2 px-2 lg:px-3 rounded-lg text-xs lg:text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center space-x-1">
                          <ArrowDownTrayIcon className="w-3 h-3 lg:w-4 lg:h-4" />
                          <span>PNG</span>
                        </button>
                        <button onClick={() => handleDownload("jpg")} className="bg-orange-600 text-white py-2 px-2 lg:px-3 rounded-lg text-xs lg:text-sm font-medium hover:bg-orange-700 transition-colors flex items-center justify-center space-x-1">
                          <ArrowDownTrayIcon className="w-3 h-3 lg:w-4 lg:h-4" />
                          <span>JPG</span>
                        </button>
                        <button onClick={() => handleDownload("webp")} className="bg-purple-600 text-white py-2 px-2 lg:px-3 rounded-lg text-xs lg:text-sm font-medium hover:bg-purple-700 transition-colors flex items-center justify-center space-x-1">
                          <ArrowDownTrayIcon className="w-3 h-3 lg:w-4 lg:h-4" />
                          <span>WEBP</span>
                        </button>
                      </div>
                      
                      <button 
                        onClick={handlePrint} 
                        className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-gray-700 transition-colors flex items-center justify-center space-x-2"
                      >
                        <PrinterIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                        <span className="text-sm lg:text-base">{t.print}</span>
                      </button>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>


          </div>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="text-center py-8 text-gray-600 dark:text-gray-400 text-sm qr-fade-in">
        <div className="container mx-auto px-4">
          <p className="flex items-center justify-center gap-2">
            <span>&copy; {new Date().getFullYear()}</span>
            <a 
              href="https://oz.sa"
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-300"
            >
              أسامة الزبيدي
            </a>
            <span>صنع بكل</span>
            <a
              href="https://www.snapchat.com/add/zk9c"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center text-pink-500 hover:text-pink-600 focus:outline-none focus:ring-2 focus:ring-pink-300 transition-colors duration-300 text-lg"
              aria-label="تابعنا على السناب شات"
            >
              ❤️
            </a>
            <span>في</span>
            <span className="text-lg">🇸🇦</span>
          </p>
        </div>
      </footer>
    </div>
  );
} 